/**
 * Universal API Client
 *
 * Provides access to all three REST API endpoints:
 * - /api/v1/clubs
 * - /api/v1/valuations
 * - /api/v1/player-sales
 *
 * Features:
 * - Unified interface for all endpoints
 * - Automatic caching with TTL
 * - Parallel data fetching
 * - Comprehensive report generation
 * - Error handling with APIError
 */

import {
  UniversalAPIClient as IUniversalAPIClient,
  ClubData,
  ValuationData,
  PlayerSaleData,
  ComprehensiveReportData,
  PluginCache,
  APIError,
} from '@/lib/platform/types/plugins';

export class UniversalAPIClient implements IUniversalAPIClient {
  constructor(
    private apiKey: string,
    private baseUrl: string = '/api/v1',
    private cache?: PluginCache
  ) {}

  // ============================================================================
  // Clubs Endpoint
  // ============================================================================

  async getClub(organizationId: string): Promise<ClubData> {
    const cacheKey = `club:${organizationId}`;

    // Try cache first
    if (this.cache) {
      const cached = await this.cache.get<ClubData>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    // Fetch from API
    const url = `${this.baseUrl}/clubs/${organizationId}?api_key=${encodeURIComponent(this.apiKey)}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new APIError(`Failed to fetch club: ${response.statusText}`, response.status, url);
      }

      const data = await response.json();

      // Cache for 1 hour (club data rarely changes)
      if (this.cache) {
        await this.cache.set(cacheKey, data, 3600);
      }

      return data;
    } catch (error: unknown) {
      if (error instanceof APIError) {
        throw error;
      }
      const errorMessage = (error as Error).message || 'Unknown error';
      throw new APIError(`Network error fetching club: ${errorMessage}`, 0, url);
    }
  }

  // ============================================================================
  // Valuations Endpoint
  // ============================================================================

  async getValuations(
    organizationId: string,
    options?: {
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<ValuationData[]> {
    const params = new URLSearchParams({
      organization_id: organizationId,
      api_key: this.apiKey,
    });

    if (options?.fromDate) params.append('from_date', options.fromDate);
    if (options?.toDate) params.append('to_date', options.toDate);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const url = `${this.baseUrl}/valuations?${params}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new APIError(
          `Failed to fetch valuations: ${response.statusText}`,
          response.status,
          url
        );
      }

      const data = await response.json();

      // API returns { valuations: [...] }
      return data.valuations || [];
    } catch (error: unknown) {
      if (error instanceof APIError) {
        throw error;
      }
      const errorMessage = (error as Error).message || 'Unknown error';
      throw new APIError(`Network error fetching valuations: ${errorMessage}`, 0, url);
    }
  }

  // ============================================================================
  // Player Sales Endpoint
  // ============================================================================

  async getPlayerSales(
    organizationId: string,
    options?: {
      valuationId?: string;
      fromDate?: string;
      toDate?: string;
    }
  ): Promise<PlayerSaleData[]> {
    const params = new URLSearchParams({
      organization_id: organizationId,
      api_key: this.apiKey,
    });

    if (options?.valuationId) params.append('valuation_id', options.valuationId);
    if (options?.fromDate) params.append('from_date', options.fromDate);
    if (options?.toDate) params.append('to_date', options.toDate);

    const url = `${this.baseUrl}/player-sales?${params}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new APIError(
          `Failed to fetch player sales: ${response.statusText}`,
          response.status,
          url
        );
      }

      const data = await response.json();

      // API returns { playerSales: [...] } or { player_sales: [...] }
      return data.playerSales || data.player_sales || [];
    } catch (error: unknown) {
      if (error instanceof APIError) {
        throw error;
      }
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new APIError(`Network error fetching player sales: ${errorMessage}`, 0, url);
    }
  }

  // ============================================================================
  // Comprehensive Report (All Endpoints Combined)
  // ============================================================================

  async getComprehensiveReport(
    organizationId: string,
    options?: {
      fromDate?: string;
      toDate?: string;
    }
  ): Promise<ComprehensiveReportData> {
    try {
      // Fetch all data in parallel for performance
      const [club, valuations, playerSales] = await Promise.all([
        this.getClub(organizationId),
        this.getValuations(organizationId, options),
        this.getPlayerSales(organizationId, options),
      ]);

      // Calculate summary statistics
      const summary = this.calculateSummary(valuations, playerSales);

      return {
        club,
        valuations,
        playerSales,
        summary,
      };
    } catch (error: unknown) {
      if (error instanceof APIError) {
        throw error;
      }
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new APIError(`Failed to generate comprehensive report: ${errorMessage}`, 0);
    }
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private calculateSummary(valuations: ValuationData[], playerSales: PlayerSaleData[]) {
    const totalValuations = valuations.length;
    const totalSales = playerSales.length;

    // Calculate average valuation
    const averageValue =
      totalValuations > 0
        ? valuations.reduce((sum, v) => sum + v.final_total_value, 0) / totalValuations
        : 0;

    // Calculate trend from last 2 valuations
    let trend: 'up' | 'down' | 'stable' = 'stable';

    if (valuations.length >= 2) {
      // Sort by date to ensure correct order
      const sorted = [...valuations].sort(
        (a, b) => new Date(b.valuation_date).getTime() - new Date(a.valuation_date).getTime()
      );

      const current = sorted[0].final_total_value;
      const previous = sorted[1].final_total_value;

      if (previous > 0) {
        const changePercent = ((current - previous) / previous) * 100;

        if (changePercent > 5) {
          trend = 'up';
        } else if (changePercent < -5) {
          trend = 'down';
        }
      }
    }

    return {
      totalValuations,
      totalSales,
      averageValue,
      trend,
    };
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Test API connection and credentials
   */
  async testConnection(): Promise<boolean> {
    try {
      // Simple test: try to fetch clubs endpoint
      const response = await fetch(
        `${this.baseUrl}/clubs?api_key=${encodeURIComponent(this.apiKey)}`
      );
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Get API base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Clear all cached data
   */
  async clearCache(): Promise<void> {
    if (this.cache) {
      await this.cache.clear();
    }
  }
}
